import _ from "lodash";
import React, { useState, useEffect } from "react";
import {
  Modal,
  DSProvider,
  IconButton,
  Tooltip,
  Button,
  Checkbox,
} from "@toddle-edu/ds-web";
import EventTitle from "../EventTitle";
import DateTime from "../DateTime";
import RepeatEvent from "../RepeatEvent";
import MeetingLink from "../MeetingLink";
import EventParticipants from "../ParticipantsV2";
import Attachments from "../Attachments";
import classes from "./VideoMeetingCreationModal.scss";
import useVideoMeetingModal from "../../hooks/useVideoMeetingModal";
import useScrollToBottom from "../../hooks/useScrollToBottom";
import classNames from "classnames";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import {
  mapStateToProps,
  mapActionCreators,
  Z_INDEX,
  EVENT_MODE_LABELS_VIDEO_MEETING,
  setValue,
} from "../../modules/Utils";
import { connect } from "react-redux";
import { resetEventModalV2 } from "MyCalendar/modules/MyCalendarModule";
import FullScreenVideoMeetingModal from "../FullScreenVideoMeetingModal";
import ConformationModal from "../ConfirmationModal";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { getSelectedAcademicYearStartDate } from "modules/CommonSelectors";
import { useOperationalDays } from "../../hooks";
import { getSelectedAcademicYearForSchool } from "../../../../modules/Utils";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";

const NotifyContainer = ({ t, eventModalV2, updateEventModalV2, testId }) => {
  const [shouldNotifyParticipants, setShouldNotifyParticipants] = useState(
    eventModalV2?.shouldNotifyParticipants
  );

  const toggleNotifyParticipants = setValue(
    "shouldNotifyParticipants",
    updateEventModalV2
  );
  const onChangeHandler = () => {
    setShouldNotifyParticipants(!shouldNotifyParticipants);
    toggleNotifyParticipants(!shouldNotifyParticipants);
  };
  return (
    <div className={classes.notifyContainer}>
      <Checkbox
        isChecked={shouldNotifyParticipants}
        dsVersion="2.0"
        size="medium"
        onClick={onChangeHandler}
        testId={`${testId}-notify-participants`}
      />
      <div className={classNames(classes.notifyLabel, "text-body")}>
        {t("calendar:notify_participants_on_creation_of_the_meeting")}
      </div>
    </div>
  );
};

const VideoMeetingCreationModal = props => {
  const {
    createEventContainerClass,
    testId = "",
    eventModalV2,
    updateEventModalV2,
  } = props;

  useEffect(() => {
    updateEventModalV2({
      calendarEventItemType: MY_CALENDAR_CONSTANTS.VIDEO_MEETING,
    });
  }, []);

  const {
    t,
    isModalOpen,
    setIsModalOpen,
    isFullScreenModal,
    closeModal,
    openFullScreenModal,
    showModal,
    isConfirmationModalOpen,
    onDiscardingChanges,
    onCancelDiscardingChanges,
    mode,
    actionButtons,
    showFullScreenLoader,
    isEditMode,
    isRepeatEvent,
  } = useVideoMeetingModal(props);

  const operationalDays = useOperationalDays();

  const [scrollRef] = useScrollToBottom();

  const labelHeadingClasses = classNames("text-label-s", classes.headerHeading);

  const renderModalContent = () => {
    if (isFullScreenModal) {
      return (
        <FullScreenVideoMeetingModal
          {...props}
          t={t}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isFullScreenModal={isFullScreenModal}
          closeModal={closeModal}
          openFullScreenModal={openFullScreenModal}
          showModal={showModal}
          isConfirmationModalOpen={isConfirmationModalOpen}
          onDiscardingChanges={onDiscardingChanges}
          onCancelDiscardingChanges={onCancelDiscardingChanges}
          mode={mode}
          actionButtons={actionButtons}
          showFullScreenLoader={showFullScreenLoader}
          isEditMode={isEditMode}
          isRepeatEvent={isRepeatEvent}
          operationalDays={operationalDays}
          testId={testId}
        />
      );
    }

    if (showModal) {
      return (
        <Modal
          width="660px"
          height="max-content"
          setIsModalOpen={setIsModalOpen}
          isOpen={isModalOpen}
          onClose={closeModal}
          zIndex={Z_INDEX}
        >
          <Modal.Header isClosable={false} isSticky={true}>
            <div className={classes.container}>
              <div className={classes.left}>
                <div className={labelHeadingClasses}>
                  {t(
                    _.get(
                      EVENT_MODE_LABELS_VIDEO_MEETING,
                      mode,
                      "calendar:new_event"
                    )
                  )}
                </div>
                <EventTitle
                  testId={`${testId}`}
                  placeholder={t("calendar:add_title")}
                />
              </div>
              <div className={classes.right}>
                <Tooltip
                  dsVersion="2.0"
                  tooltip={t("common:close")}
                  showArrow={true}
                  placement="bottom"
                >
                  <IconButton
                    variant="neutral"
                    type="plain"
                    onClick={closeModal}
                    icon={<CloseOutlined variant="subtle" />}
                    size="medium"
                  />
                </Tooltip>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body isScrollable={true}>
            <div className={classes.modalBody}>
              <DateTime
                showAllDayCheckbox={false}
                isAllDayEvent={false}
                repeatLabel={t("calendar:repeat_meeting")}
              />
              {isRepeatEvent ? (
                <RepeatEvent
                  dropdownContainerClass={classes.dropdownContainerClass}
                  skipNonOperationalDaysClassName={
                    classes.skipNonOperationalDays
                  }
                  operationalDays={operationalDays}
                  repeatTitleLocaleKey={"calendar:repeat_meeting"}
                />
              ) : null}
              <div className={classes.participants}>
                <EventParticipants testId={`${testId}`} />
              </div>
              <div className={classes.meetingLink}>
                <MeetingLink testId={`${testId}`} showSeparator={false} />
              </div>
              <Attachments testId={`${testId}`} />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.footer}>
              <NotifyContainer
                t={t}
                eventModalV2={eventModalV2}
                updateEventModalV2={updateEventModalV2}
                testId={testId}
              />
              <div className={classes.footerActionContainer}>
                <Button
                  onClick={openFullScreenModal}
                  variant="neutral"
                  type="outlined"
                  testId={`${testId}-more-options`}
                >
                  {t("common:more_options")}
                </Button>
                {_.get(actionButtons, mode, null)}
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      );
    }
    return (
      <div
        className={classNames(
          classes.customModalContainer,
          createEventContainerClass
        )}
      >
        <div className={classes.customModalHeader}>
          <div className={classes.container}>
            <div className={classes.left}>
              <div className={labelHeadingClasses}>
                {t(
                  _.get(
                    EVENT_MODE_LABELS_VIDEO_MEETING,
                    mode,
                    "calendar:new_event"
                  )
                )}
              </div>
              <EventTitle
                testId={`${testId}`}
                placeholder={t("calendar:add_title")}
              />
            </div>
            <div className={classes.right}>
              <Tooltip
                dsVersion="2.0"
                tooltip={t("common:close")}
                showArrow={true}
                placement="bottom"
              >
                <IconButton
                  variant="neutral"
                  type="plain"
                  onClick={closeModal}
                  icon={<CloseOutlined variant="subtle" />}
                  size="medium"
                />
              </Tooltip>
            </div>
          </div>
        </div>
        <div ref={scrollRef} className={classes.customModalBody}>
          <DateTime
            showAllDayCheckbox={false}
            isAllDayEvent={false}
            repeatLabel={t("calendar:repeat_meeting")}
            isSelectedSlotVisible={true}
          />
          {isRepeatEvent ? (
            <RepeatEvent
              dropdownContainerClass={classes.dropdownContainerClass}
              skipNonOperationalDaysClassName={classes.skipNonOperationalDays}
              operationalDays={operationalDays}
              repeatTitleLocaleKey={"calendar:repeat_meeting"}
            />
          ) : null}
          <div className={classes.participants}>
            <EventParticipants testId={`${testId}`} />
          </div>
          <div className={classes.meetingLink}>
            <MeetingLink testId={`${testId}`} showSeparator={false} />
          </div>
          <Attachments testId={`${testId}`} />
        </div>
        <div className={classes.customFooter}>
          <NotifyContainer
            t={t}
            eventModalV2={eventModalV2}
            updateEventModalV2={updateEventModalV2}
            testId={testId}
          />
          <div className={classes.customModalfooter}>
            <Button
              onClick={openFullScreenModal}
              variant="neutral"
              type="outlined"
              testId={`${testId}-more-options`}
            >
              {t("common:more_options")}
            </Button>
            {_.get(actionButtons, mode, null)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DSProvider dsVersion="2.0">
      {renderModalContent()}
      <ConformationModal
        isConfirmationModalOpen={isConfirmationModalOpen}
        t={t}
        onCancelDiscardingChanges={onCancelDiscardingChanges}
        onDiscardingChanges={onDiscardingChanges}
      />
      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </DSProvider>
  );
};

export const localMapActionCreators = {
  ...mapActionCreators,
  resetEventModalV2,
};

const localMapStateToProps = state => {
  const selectedAcademicYear = getSelectedAcademicYearForSchool(state);
  const selectedAcademicYearStartDate = getSelectedAcademicYearStartDate({
    selectedAcademicYear,
  });
  return {
    ...mapStateToProps(state),
    selectedAcademicYearStartDate,
  };
};

export default connect(
  localMapStateToProps,
  localMapActionCreators
)(VideoMeetingCreationModal);
